/**
 * This is what powers the analytics dashboard,
 * for logged-out hits
 * This file is meant to be a modern replacement for
 * js/core_pre_webpack/metrics/record_hit.wjs.jsx.
 * It has all the same functionality.
 * Bonus: no jQuery.
 * This file populates two global variables:
 * - window.Aedu.hit_data
 * - window.$auvid
 */

import Cookies from 'js-cookie';
import { getRequestId } from './getRequestId';
import { recordHit } from './record_hit';
import { consoleLog } from './console';

declare global {
  interface Window {
    // Set here (ideally) or in core_webpack.wjs.jsx for some legacy pages
    $auvid?: string;
  }
}

/**
 * Returns a Promise that resolves when the DOM is ready.
 * If the DOM is already ready, waits for the next tick to ensure
 * all existing DOMContentLoaded handlers have run first.
 */
function domLoadedPromise(): Promise<void> {
  return new Promise((resolve) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => resolve(), {
        once: true,
      });
    } else {
      // DOM is already ready, but wait for next tick to ensure
      // all existing DOMContentLoaded handlers have run
      setTimeout(resolve, 0);
    }
  });
}

export const loadRecordHit = async (
  forceNewHit = false,
  referrer = document.referrer,
) => {
  window.Aedu = window.Aedu ?? {};

  // Wait for DOM to be ready and all existing handlers to run. This is because, when using the
  // simple_cacheable.html.erb template, we have to wait for its DOMContentLoaded handler to
  // generate the csrf_meta_tags elements before we call fetchWithCsrf()
  await domLoadedPromise();

  return setUpHitTracking(forceNewHit, referrer);
};

const setUpHitTracking = async (forceNewHit: boolean, referrer: string) => {
  setAuvid();
  await handleRecordHit(forceNewHit, referrer);
  Cookies.set('from_cache', '');
};

const setAuvid = () => {
  const auvidCookieKey = 'auvid';
  const auvidCookie = Cookies.get(auvidCookieKey);
  if (auvidCookie) {
    window.$auvid = auvidCookie;
  } else {
    const auvidNewVal = btoa(new Date().getTime() + ':' + Math.random());
    const domain = window.location.hostname.match(/(?:www\.)?(.*)/);

    Cookies.set(auvidCookieKey, auvidNewVal, {
      path: '/',
      expires: 30,
      domain: '.' + domain,
    });

    window.$auvid = auvidNewVal;
  }
};

const handleRecordHit = async (forceNewHit: boolean, referrer: string) => {
  if (!forceNewHit && window.Aedu.hit_data) {
    return;
  }
  const allowFrontendNavigationHits = forceNewHit;
  const respData = await recordHit(
    {
      url: window.location.href,
      referrer: referrer,
      request_id: getRequestId(),
    },
    allowFrontendNavigationHits,
  );

  if (respData === null) return;

  window.Aedu.hit_data = respData;

  if (respData.auvid !== window.$auvid) {
    consoleLog(
      'Unexpected auvid mismatch: client=' +
        window.$auvid +
        ' & server=' +
        respData.auvid,
    );
  }
};
